import { Layout } from "../../components/Layout";
import { PageHeader } from "../../components/PageHeader";
import { MetricCard } from "../../components/MetricCard";
import { RiskBadge } from "../../components/RiskBadge";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { NetWorthChart } from "../../components/dashboard/NetWorthChart";
import { CashForecastChart } from "../../components/dashboard/CashForecastChart";
import { MrrCard } from "../../components/dashboard/MrrCard";
import { ChurnAlertsCard } from "../../components/dashboard/ChurnAlertsCard";
import { LineItemCategoriesCard } from "../../components/dashboard/LineItemCategoriesCard";
import { FxPnlCard } from "../../components/dashboard/FxPnlCard";
import { UserPushOptInBanner } from "../../components/pwa/UserPushOptInBanner";
import { FeatureUsageAlert } from "../../components/dashboard/FeatureUsageAlert";
import { WorkspacePlanUpsellBanner } from "../../components/dashboard/WorkspacePlanUpsellBanner";
import { WorkspaceNameEditor } from "../../components/dashboard/WorkspaceNameEditor";
import { SharedFxApprovalBanner } from "../../components/dashboard/SharedFxApprovalBanner";
import { StreakChip } from "../../components/dashboard/GamificationChips";
import { formatMoney } from "../../lib/api";
import { useDashboard } from "../../hooks/queries";
import { useLang } from "../../contexts/LangContext";
import { useAuth } from "../../contexts/AuthContext";
import { tx } from "../../i18n";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { trackDemoEvent } from "../../lib/demoEvents";
import {
  Wallet, ArrowDown, ArrowUp, PiggyBank, Banknote, Lightbulb, CalendarClock, ArrowRight, RefreshCw,
  LineChart, CreditCard, Target,
} from "lucide-react";

export default function Dashboard() {
  const { t, translateAction, lang } = useLang();
  const { user } = useAuth();
  const { data: snap, isLoading: loading, refetch } = useDashboard();
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  // iter-205 — Fire safe_number_viewed exactly once per session the
  // first time the user sees a populated safe-pay card. This is THE
  // aha-moment event the funnel cares about most.
  useEffect(() => {
    if (snap && snap.safe_to_use_this_week !== undefined) {
      trackDemoEvent("safe_number_viewed", {
        slug: user?.demo_slug,
        email: user?.email,
        extra: { safe_amount: snap.safe_to_use_this_week, is_demo: !!user?.is_demo },
      }, { once: true });
    }
  }, [snap, user?.demo_slug, user?.email, user?.is_demo]);

  const ccy = snap?.currency || "USD";

  const handleBreakdownClick = () => {
    setBreakdownOpen((cur) => !cur);
    trackDemoEvent("cash_breakdown_opened", {
      slug: user?.demo_slug,
      email: user?.email,
    }, { once: true });
  };

  return (
    <Layout>
      <PageHeader
        eyebrow={t("dashboard_scope_badge")}
        title={t("dashboard")}
        subtitle={t("dashboard_scope_sub")}
        testid="dashboard-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <WorkspaceNameEditor />
            <StreakChip lang={lang} userName={user?.name || user?.first_name || ""} />
            <Button data-testid="dashboard-refresh-btn" variant="outline" size="sm" onClick={() => refetch()} className="gap-2 rounded-xl">
              <RefreshCw className="h-4 w-4" /> {tx(lang, "Refresh", "Actualizar", "Atualizar")}
            </Button>
          </div>
        }
      />

      {/* iter-169r — one-shot push notifications opt-in */}
      <div className="mb-4">
        <UserPushOptInBanner lang={lang} />
      </div>

      {/* iter-170an follow-up — per-workspace plan upsell.
          Renders only when the ACTIVE workspace has no paid plan
          (composition is null or has no primary_plan_id). Dismissable
          per session. Drives zero-click conversion to /pricing where
          the workspace selector is pre-scoped to this same space. */}
      <WorkspacePlanUpsellBanner lang={lang} />

      {/* iter-170an follow-up #5 — Shared ops awaiting FX approval. */}
      <SharedFxApprovalBanner lang={lang} />

      {/* iter-169z3 — low-balance nudge for metered features (recibos IA, etc.) */}
      <FeatureUsageAlert lang={lang} />

      {loading && <div className="text-slate-500">{t("loading")}</div>}

      {snap && (
        <div className="space-y-4">
          {/* Hero cards */}
          <div className="grid sm:grid-cols-2 gap-4">
            <MetricCard
              testid="metric-real-cash"
              label={t("real_cash")}
              value={<span data-testid="real-cash-value">{formatMoney(snap.real_available_cash, ccy)}</span>}
              sub={`${t("current_cash")}: ${formatMoney(snap.current_cash, ccy)}`}
              icon={<Wallet className="h-4 w-4" />}
              className="fade-up"
              onClick={handleBreakdownClick}
            />
            <MetricCard
              testid="metric-safe-week"
              label={t("safe_to_use")}
              value={<span data-testid="safe-week-value">{formatMoney(snap.safe_to_use_this_week, ccy)}</span>}
              sub={`${Math.round((snap.safety_factor || 0) * 100)}% ${t("of_real_cash")}`}
              icon={<Banknote className="h-4 w-4" />}
              variant="dark"
              className="fade-up-1"
            />
          </div>

          {/* Risk + Recommendation */}
          <Card className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 fade-up-2" data-testid="recommendation-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-emerald-50 dark:bg-emerald-950 flex items-center justify-center">
                  <Lightbulb className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400">{t("monthly_risk")}</div>
                  <div className="mt-1"><RiskBadge risk={snap.risk_level} size="lg" /></div>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400">{t("recommended_action")}</div>
              <p className="mt-1.5 text-base text-slate-900 dark:text-slate-100 leading-relaxed" data-testid="recommended-action-text">
                {translateAction(snap.recommended_action_code, snap.recommended_action_params, snap.recommended_action, formatMoney, ccy)}
              </p>
            </div>
            <div className="mt-5 flex gap-2 flex-wrap">
              <Link to="/receivables"><Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="goto-receivables-btn">{t("nav_receivables")} <ArrowRight className="h-3.5 w-3.5" /></Button></Link>
              <Link to="/payments"><Button size="sm" variant="outline" className="gap-2" data-testid="goto-payments-btn">{t("nav_payments")}</Button></Link>
              <Link to="/report"><Button size="sm" variant="ghost" className="gap-2" data-testid="goto-report-btn">{t("weekly_report")}</Button></Link>
            </div>
          </Card>

          {/* Breakdown */}
          <h2 className="font-display text-xl font-bold text-slate-900 dark:text-slate-100 pt-4">{t("financial_breakdown")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <BreakItem icon={<Wallet className="h-4 w-4" />} label={t("current_cash")} value={formatMoney(snap.current_cash, ccy)} testid="break-current-cash" />
            <BreakItem icon={<ArrowDown className="h-4 w-4 text-emerald-700" />} label={t("expected_receivables")} value={`+${formatMoney(snap.expected_receivables, ccy)}`} color="text-emerald-700" testid="break-recv" />
            <BreakItem icon={<ArrowUp className="h-4 w-4 text-red-600" />} label={t("upcoming_payments")} value={`-${formatMoney(snap.upcoming_payments, ccy)}`} color="text-red-600" testid="break-payments" />
            <BreakItem icon={<PiggyBank className="h-4 w-4" />} label={t("suggested_reserve")} value={`-${formatMoney(snap.suggested_reserve, ccy)}`} testid="break-reserve" />
            <BreakItem icon={<CalendarClock className="h-4 w-4" />} label={t("desired_withdrawal")} value={formatMoney(snap.desired_withdrawal, ccy)} testid="break-desired" />
          </div>

          {/* Wealth summary */}
          {(snap.investments_count > 0 || snap.debts_count > 0 || snap.goals_count > 0) && (
            <>
              <div className="flex items-center justify-between pt-4">
                <h2 className="font-display text-xl font-bold text-slate-900">{t("wealth_overview")}</h2>
                <div className="text-sm text-slate-500">
                  {t("net_worth")}: <span className="font-display font-bold text-slate-900 tabular-nums">{formatMoney(snap.net_worth, ccy)}</span>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Link to="/investments" data-testid="wealth-investments-card" className="group">
                  <Card className="p-4 rounded-2xl border-slate-200 hover:border-emerald-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 h-full cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="h-9 w-9 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center"><LineChart className="h-4 w-4" /></div>
                      <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <div className="mt-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">{t("investments")}</div>
                    <div className="font-display text-xl font-bold tabular-nums mt-0.5">{formatMoney(snap.investments_value, ccy)}</div>
                    <div className="text-xs text-slate-500 mt-1">{snap.investments_count} {t("items")}</div>
                  </Card>
                </Link>
                <Link to="/debts" data-testid="wealth-debts-card" className="group">
                  <Card className="p-4 rounded-2xl border-slate-200 hover:border-rose-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 h-full cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="h-9 w-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center"><CreditCard className="h-4 w-4" /></div>
                      <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-rose-600 group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <div className="mt-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">{t("debts")}</div>
                    <div className="font-display text-xl font-bold tabular-nums mt-0.5 text-red-600">{formatMoney(snap.debts_total_balance, ccy)}</div>
                    <div className="text-xs text-slate-500 mt-1">{snap.debts_count} • {formatMoney(snap.debts_monthly_payment, ccy)}/mo</div>
                  </Card>
                </Link>
                <Link to="/goals" data-testid="wealth-goals-card" className="group">
                  <Card className="p-4 rounded-2xl border-slate-200 hover:border-sky-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 h-full cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="h-9 w-9 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center"><Target className="h-4 w-4" /></div>
                      <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-sky-600 group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <div className="mt-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">{t("goals")}</div>
                    <div className="font-display text-xl font-bold tabular-nums mt-0.5">{formatMoney(snap.goals_saved, ccy)}</div>
                    <div className="text-xs text-slate-500 mt-1">{tx(lang, "of", "de", "de")} {formatMoney(snap.goals_target, ccy)}</div>
                  </Card>
                </Link>
              </div>
            </>
          )}

          {/* Net worth time-series chart */}
          <NetWorthChart />

          {/* iter-211 — Monthly recurring revenue (hidden when count=0). */}
          <MrrCard />

          {/* iter-211 — Subscription churn alerts (hidden when no subs ever). */}
          <ChurnAlertsCard />

          {/* Forward-looking cash forecast */}
          <CashForecastChart />

          {/* Monthly FX gain/loss across all foreign-currency movements. */}
          <FxPnlCard />

          {/* Inferred-category breakdown of line items (hidden when empty). */}
          <LineItemCategoriesCard />
        </div>
      )}
    </Layout>
  );
}

function BreakItem({ icon, label, value, color, testid }) {
  return (
    <div data-testid={testid} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 flex items-center gap-3 hover:shadow-sm transition-shadow">
      <div className="h-9 w-9 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 flex items-center justify-center">{icon}</div>
      <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{label}</div>
        <div className={`font-display text-lg font-bold tabular-nums ${color || "text-slate-900 dark:text-slate-100"}`}>{value}</div>
      </div>
    </div>
  );
}
